import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { ChevronDownIcon } from '@/components/layout/icons';
import { useAllServices, useGroupedServices } from '@shared/hooks/use-services';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
} from '@shared/types/api';

// Category-level copy shown above each right-side pane. Keep in sync with
// SERVICE_CATEGORIES (shared/types/api.ts).
const CATEGORY_TAGLINES: Record<ServiceCategory, string> = {
  DOCUMENTATION: 'Drafts, deeds, affidavits',
  BUSINESS_REGISTRATION: 'Incorporation, licenses',
  COMPLIANCE: 'Filings, returns, renewals',
  FINANCE: 'Tax, accounting, advisory',
  LEGAL: 'Disputes, agreements, notices',
};

type GroupMode = 'category' | 'authority';

interface AuthorityGroup {
  id: string;
  name: string;
  code: string | null;
  image?: string | null;
  createdAt?: string | Date | null;
  items: ServiceSummary[];
}

export function ServicesMenu() {
  const [open, setOpen] = useState(false);
  const [groupMode, setGroupMode] = useState<GroupMode>('authority');
  const [activeCat, setActiveCat] = useState<ServiceCategory>(SERVICE_CATEGORIES[0]);
  const [activeAuthId, setActiveAuthId] = useState<string | null>(null);

  const { data: categoryData } = useGroupedServices();
  const { data: allServices = [] } = useAllServices();

  // Group services by government authority (ordered by creation order to match Admin panel)
  const authorityGroups = useMemo(() => {
    const map = new Map<string, AuthorityGroup>();

    for (const s of allServices) {
      if (!s.authority) continue;

      const authId = s.authority.id;
      const authName = s.authority.name;
      const authCode = s.authority.code ?? null;
      const authImage = s.authority.images?.[0] ?? null;
      const authCreatedAt = s.authority.createdAt ?? null;

      if (!map.has(authId)) {
        map.set(authId, {
          id: authId,
          name: authName,
          code: authCode,
          image: authImage,
          createdAt: authCreatedAt,
          items: [],
        });
      } else {
        const item = map.get(authId)!;
        if (!item.image && authImage) item.image = authImage;
        if (!item.createdAt && authCreatedAt) item.createdAt = authCreatedAt;
      }
      map.get(authId)!.items.push(s);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }, [allServices]);

  // Set default active authority once data is loaded
  useEffect(() => {
    if (
      authorityGroups.length > 0 &&
      (!activeAuthId || !authorityGroups.some((g) => g.id === activeAuthId))
    ) {
      setActiveAuthId(authorityGroups[0].id);
    }
  }, [authorityGroups, activeAuthId]);

  // Hover UX: the panel is fixed-positioned and visually separated from the
  // trigger, so a naive onMouseLeave closes the menu the moment the cursor
  // crosses the gap. A small close delay that any mouseEnter on either
  // element can cancel bridges the gap without needing a hack DOM node.
  const closeTimer = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const show = () => {
    cancelClose();
    setOpen(true);
  };

  // Pick a sensible default active category the first time real data shows up
  useEffect(() => {
    if (!categoryData) return;
    const firstNonEmpty = categoryData.groups.find((g) =>
      g.subGroups.some((sg) => sg.items.length > 0),
    )?.category;
    if (firstNonEmpty) setActiveCat(firstNonEmpty);
  }, [categoryData]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const activeCategoryGroup = categoryData?.groups.find((g) => g.category === activeCat);
  const activeAuthorityGroup =
    authorityGroups.find((g) => g.id === activeAuthId) ?? authorityGroups[0];

  return (
    <div ref={rootRef} className="relative" onMouseEnter={show} onMouseLeave={scheduleClose}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1 text-sm font-medium transition-colors',
          open ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900',
        )}
      >
        Services
        <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-30 bg-gray-900/10"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            ref={panelRef}
            onMouseEnter={show}
            onMouseLeave={scheduleClose}
            className="fixed left-1/2 top-[72px] z-40 w-[min(96vw,960px)] -translate-x-1/2"
            role="dialog"
            aria-label="Services"
          >
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5">
              <div className="grid grid-cols-[34%_66%] sm:grid-cols-[30%_70%]">
                {/* LEFT — 30-34% side rail with Tab Switcher */}
                <aside className="border-r border-gray-100 bg-gray-50/80 p-3">
                  {/* Segmented Control Toggle */}
                  <div className="mb-3 flex rounded-lg bg-gray-200/80 p-1">
                    <button
                      type="button"
                      onClick={() => setGroupMode('authority')}
                      className={cn(
                        'flex-1 rounded-md py-1 text-center text-xs font-semibold transition-all',
                        groupMode === 'authority'
                          ? 'bg-white text-brand-800 shadow-xs ring-1 ring-black/5'
                          : 'text-gray-600 hover:text-gray-900',
                      )}
                    >
                      By Authority
                    </button>
                    <button
                      type="button"
                      onClick={() => setGroupMode('category')}
                      className={cn(
                        'flex-1 rounded-md py-1 text-center text-xs font-semibold transition-all',
                        groupMode === 'category'
                          ? 'bg-white text-brand-800 shadow-xs ring-1 ring-black/5'
                          : 'text-gray-600 hover:text-gray-900',
                      )}
                    >
                      By Category
                    </button>
                  </div>

                  <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                    {groupMode === 'authority' ? 'Government Authorities' : 'Categories'}
                  </p>

                  {/* Authority or Category Rail */}
                  {groupMode === 'authority' ? (
                    /* Authority Rail */
                    <ul className="space-y-1 max-h-[360px] overflow-y-auto pr-0.5">
                      {authorityGroups.map((authGroup) => {
                        const active = authGroup.id === activeAuthId;
                        return (
                          <li key={authGroup.id}>
                            <button
                              type="button"
                              onMouseEnter={() => setActiveAuthId(authGroup.id)}
                              onFocus={() => setActiveAuthId(authGroup.id)}
                              onClick={() => setActiveAuthId(authGroup.id)}
                              className={cn(
                                'group flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                                active
                                  ? 'bg-white text-brand-800 shadow-sm ring-1 ring-gray-200'
                                  : 'text-gray-700 hover:bg-white/70',
                              )}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1 truncate">
                                {authGroup.image ? (
                                  <img
                                    src={authGroup.image}
                                    alt={authGroup.name}
                                    className="h-4 w-4 rounded object-contain shrink-0"
                                  />
                                ) : null}
                                <span className="block truncate font-medium text-xs sm:text-sm">
                                  {authGroup.name}
                                </span>
                              </div>
                              <span
                                className={cn(
                                  'flex items-center gap-1 text-xs shrink-0',
                                  active ? 'text-brand-700 font-semibold' : 'text-gray-400',
                                )}
                              >
                                <span className="tabular-nums">{authGroup.items.length}</span>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden
                                >
                                  <path d="m9 18 6-6-6-6" />
                                </svg>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    /* Category Rail */
                    <ul className="space-y-1">
                      {(
                        categoryData?.groups ??
                        SERVICE_CATEGORIES.map((c) => ({ category: c, subGroups: [] }))
                      ).map((group) => {
                        const active = group.category === activeCat;
                        const totalItems = group.subGroups.reduce(
                          (n, sg) => n + sg.items.length,
                          0,
                        );
                        return (
                          <li key={group.category}>
                            <button
                              type="button"
                              onMouseEnter={() => setActiveCat(group.category)}
                              onFocus={() => setActiveCat(group.category)}
                              onClick={() => setActiveCat(group.category)}
                              className={cn(
                                'group flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                                active
                                  ? 'bg-white text-brand-800 shadow-sm ring-1 ring-gray-200'
                                  : 'text-gray-700 hover:bg-white/70',
                              )}
                            >
                              <span className="min-w-0 truncate font-medium">
                                {SERVICE_CATEGORY_LABELS[group.category]}
                              </span>
                              <span
                                className={cn(
                                  'flex items-center gap-1.5 text-xs',
                                  active ? 'text-brand-700' : 'text-gray-400',
                                )}
                              >
                                <span className="tabular-nums">{totalItems}</span>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden
                                >
                                  <path d="m9 18 6-6-6-6" />
                                </svg>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </aside>

                {/* RIGHT — 70% services pane */}
                <div className="p-6">
                  {groupMode === 'authority' ? (
                    /* Authority View */
                    <>
                      <div>
                        <div className="flex items-center gap-2">
                          {activeAuthorityGroup?.image ? (
                            <img
                              src={activeAuthorityGroup.image}
                              alt={activeAuthorityGroup.name}
                              className="h-6 w-6 rounded object-contain shrink-0"
                            />
                          ) : null}
                          <h3 className="text-sm font-bold text-gray-900">
                            {activeAuthorityGroup?.name}
                          </h3>
                          {activeAuthorityGroup?.code && (
                            <span className="rounded bg-brand-50 border border-brand-200 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
                              {activeAuthorityGroup.code}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {activeAuthorityGroup
                            ? `${activeAuthorityGroup.items.length} ${
                                activeAuthorityGroup.items.length === 1 ? 'service' : 'services'
                              } registered under this government authority`
                            : 'Government authority services'}
                        </p>
                      </div>

                      {activeAuthorityGroup && activeAuthorityGroup.items.length > 0 ? (
                        <div className="mt-4 max-h-[380px] space-y-2 overflow-y-auto pr-1">
                          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {activeAuthorityGroup.items.map((item) => (
                              <li key={item.id}>
                                <Link
                                  to={`/client/services/${item.id}`}
                                  onClick={() => setOpen(false)}
                                  className="group flex flex-col justify-between rounded-lg border border-gray-100 bg-gray-50/50 p-2.5 transition-all hover:border-brand-300 hover:bg-brand-50/40 hover:shadow-xs"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    {item.authorityServiceCode ? (
                                      <span className="inline-block rounded bg-emerald-100/80 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 font-mono">
                                        {item.authorityServiceCode}
                                      </span>
                                    ) : (
                                      <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                        Service
                                      </span>
                                    )}
                                    {item.timelineText ? (
                                      <span className="text-[10px] text-gray-500 font-medium">
                                        {item.timelineText}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="mt-1.5 flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-gray-900 group-hover:text-brand-800 min-w-0 truncate">
                                      {item.name}
                                    </span>
                                    <span className="text-gray-400 group-hover:text-brand-700 transition-transform group-hover:translate-x-0.5 text-xs font-bold">
                                      →
                                    </span>
                                  </div>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-xs text-gray-500">
                          No services registered under this authority yet.
                        </div>
                      )}
                    </>
                  ) : (
                    /* Category View */
                    <>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {SERVICE_CATEGORY_LABELS[activeCat]}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {CATEGORY_TAGLINES[activeCat]}
                        </p>
                      </div>

                      {activeCategoryGroup && activeCategoryGroup.subGroups.length > 0 ? (
                        <div className="mt-4 max-h-[380px] space-y-5 overflow-y-auto pr-1">
                          {activeCategoryGroup.subGroups.map((sub) => (
                            <section key={sub.subCategory}>
                              <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                                {sub.subCategory}
                              </h4>
                              <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                                {sub.items.map((item) => (
                                  <li key={item.id}>
                                    <Link
                                      to={`/client/services/${item.serviceId}`}
                                      onClick={() => setOpen(false)}
                                      className="flex items-start justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-800"
                                    >
                                      <span className="min-w-0 truncate">{item.name}</span>
                                      {item.timelineText ? (
                                        <span className="shrink-0 text-[11px] text-gray-400">
                                          {item.timelineText}
                                        </span>
                                      ) : null}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </section>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-xs text-gray-500">
                          Services in this category are coming soon. In the meantime,{' '}
                          <a
                            href="mailto:support@ecregi.com"
                            className="text-brand-700 underline underline-offset-2"
                          >
                            reach out to support
                          </a>{' '}
                          and we&rsquo;ll help you directly.
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-6 py-3 text-xs">
                <span className="text-gray-500">
                  Can&rsquo;t find what you need?{' '}
                  <a
                    href="mailto:support@ecregi.com"
                    className="text-brand-700 underline underline-offset-2"
                  >
                    Ask support
                  </a>
                </span>
                <Link
                  to="/client/services"
                  onClick={() => setOpen(false)}
                  className="font-medium text-brand-700 hover:underline"
                >
                  Browse all services →
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
